import { Loader2 } from "lucide-react";
import { useUploadCsv } from "@/hooks/use-import";
import { SAMPLE_ROW_COUNT } from "@/lib/csv-sample";
import type { CsvValidationResponse, ImportEntityType } from "@adva/types";
import { Button } from "@/components/ui/button";
import { StepCounter } from "../components/step-counter";
import { formatEntityType } from "../../labels";

interface StepConfirmProps {
  entityType: ImportEntityType;
  file: File;
  validation: CsvValidationResponse;
  totalRows: number | null;
  onBack: () => void;
  onSubmitted: () => void;
  onError: (message: string) => void;
}

export function StepConfirm({
  entityType,
  file,
  validation,
  totalRows,
  onBack,
  onSubmitted,
  onError,
}: StepConfirmProps) {
  const uploadMutation = useUploadCsv();

  const handleSubmit = async () => {
    try {
      await uploadMutation.mutateAsync({
        entityType,
        file,
        source: "web_upload",
      });
      onSubmitted();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Import failed");
    }
  };

  return (
    <div className="space-y-5">
      <StepCounter current={4} total={4} label="Confirm & submit" />

      <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-[color:var(--color-text-md)]">Entity</dt>
          <dd className="font-medium">{formatEntityType(entityType)}</dd>
          <dt className="text-[color:var(--color-text-md)]">File</dt>
          <dd className="font-medium truncate">{file.name}</dd>
          {totalRows !== null && (
            <>
              <dt className="text-[color:var(--color-text-md)]">
                Rows in file
              </dt>
              <dd className="font-medium tabular-nums">
                {totalRows.toLocaleString()}
              </dd>
            </>
          )}
          <dt className="text-[color:var(--color-text-md)]">
            Validated sample (first {SAMPLE_ROW_COUNT})
          </dt>
          <dd className="font-medium tabular-nums">
            {validation.valid_rows.toLocaleString()} valid ·{" "}
            {validation.error_rows.toLocaleString()} with errors
          </dd>
        </dl>
      </div>

      <div className="flex justify-between border-t border-[color:var(--color-border)] pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={uploadMutation.isPending}
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={uploadMutation.isPending}
        >
          {uploadMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          {uploadMutation.isPending ? "Submitting…" : "Submit import"}
        </Button>
      </div>
    </div>
  );
}
